"use client";

import { type TokenAccountInfo, formatTokenAmount, shortenAddress, estimateTransactionFee } from "@/lib/burn";
import { Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const SOLSCAN_URL = "https://solscan.io";

interface BurnAmountInputProps {
  token: TokenAccountInfo;
  amount: string;
  onAmountChange: (amount: string) => void;
  onBurnClick: () => void;
  onBurnAllClick: () => void;
  isValid: boolean;
}

export function BurnAmountInput({
  token,
  amount,
  onAmountChange,
  onBurnClick,
  onBurnAllClick,
  isValid,
}: BurnAmountInputProps) {
  const tokenName = token.symbol || token.name || shortenAddress(token.mint.toBase58(), 4);
  const maxAmount = formatTokenAmount(token.amount, token.decimals);

  const setPercentage = (percent: number) => {
    const max = parseFloat(maxAmount);
    const value = max * (percent / 100);
    onAmountChange(value.toString());
  };

  const copyMint = () => {
    navigator.clipboard.writeText(token.mint.toBase58());
    toast.success("Copied to clipboard");
  };

  return (
    <div className="bg-[#080808] rounded-xl border border-[#1a1a1a] p-6 mt-2">
      <h2 className="text-base font-semibold text-white mb-4">
        Burn {tokenName}
      </h2>

      {/* Selected token display */}
      <div className="flex items-center gap-3 mb-5 pb-4 border-b border-[#222]">
        {token.logoURI ? (
          <img
            src={token.logoURI}
            alt={tokenName}
            className="w-12 h-12 rounded-full"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
        ) : (
          <div className="w-12 h-12 rounded-full bg-[#222] flex items-center justify-center text-[#666] font-semibold text-lg">
            {(token.symbol?.[0] || "?").toUpperCase()}
          </div>
        )}
        <div>
          <div className="font-semibold text-lg">{tokenName}</div>
          <div
            className="text-xs text-[#666] cursor-pointer hover:text-[#888] flex items-center gap-1"
            onClick={copyMint}
          >
            {shortenAddress(token.mint.toBase58(), 8)}
            <Copy className="w-3 h-3" />
          </div>
        </div>
        <a
          href={`${SOLSCAN_URL}/token/${token.mint.toBase58()}`}
          target="_blank"
          rel="noopener noreferrer"
          className="ml-auto text-[#666] hover:text-[#f9cd4f]"
          onClick={(e) => e.stopPropagation()}
        >
          <ExternalLink className="w-4 h-4" />
        </a>
      </div>

      {/* Amount input */}
      <div className="mb-3">
        <label className="block text-sm text-[#888] mb-2">Amount to burn</label>
        <input
          type="number"
          value={amount}
          onChange={(e) => onAmountChange(e.target.value)}
          placeholder="0.00"
          className="w-full p-3.5 text-xl bg-[#111] border border-[#333] rounded-lg text-white outline-none focus:border-[#f9cd4f] transition-colors"
        />
        <div className="text-xs text-[#666] mt-2">
          Available: {maxAmount}
          {token.symbol && ` ${token.symbol}`}
        </div>
      </div>

      {/* Percentage buttons */}
      <div className="flex gap-2 mb-4">
        {[25, 50, 75, 100].map((p) => (
          <button
            key={p}
            onClick={() => setPercentage(p)}
            className="flex-1 py-2 bg-[#1a1a1a] border border-[#333] rounded-md text-[#f9cd4f] text-sm font-semibold cursor-pointer hover:bg-[#222] transition-colors"
          >
            {p === 100 ? "MAX" : `${p}%`}
          </button>
        ))}
      </div>

      {/* Fee estimate */}
      <div className="text-xs text-[#666] text-center mb-4">
        Estimated fee: {estimateTransactionFee()} SOL
      </div>

      {/* Burn buttons */}
      <div className="flex gap-3">
        <button
          onClick={onBurnClick}
          disabled={!isValid}
          className={`flex-1 py-4 text-base font-bold rounded-lg transition-all ${
            isValid
              ? "bg-gradient-to-br from-[#FF4500] to-[#FF8C00] text-white cursor-pointer hover:opacity-90"
              : "bg-[#333] text-[#666] cursor-not-allowed"
          }`}
        >
          🔥 Burn
        </button>
        <button
          onClick={onBurnAllClick}
          className="flex-1 py-4 text-base font-bold rounded-lg transition-all bg-gradient-to-br from-[#22c55e] to-[#16a34a] text-white cursor-pointer hover:opacity-90"
        >
          🔥 Burn All & Close
        </button>
      </div>
      <p className="text-xs text-[#666] text-center mt-2">
        "Burn All & Close" burns 100% and reclaims ~0.002 SOL rent
      </p>
    </div>
  );
}
